import React from 'react';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { ModalTransition } from '@atlaskit/modal-dialog';
import ErrorStateOriginal from '@atlassian/jira-common-components-error-state';
import { DocumentTitle } from '@atlassian/jira-global-document-title';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { PRIORITY_SCHEME_CONFIGURATION_SOURCE_NAME } from '../common/constants';
import { PRIORITY_SCHEMES_HARD_LIMIT } from '../common/ui/constants';
import { Container } from '../common/ui/styled';
import { PrioritySchemesPageState } from '../common/ui/types';
import type { DeletePrioritySchemeProps } from '../controllers/delete-priority-scheme';
import type { PrioritySchemesPageControllerReturn } from '../controllers/priority-schemes-page';
import { DeletePrioritySchemeModalWrapper } from './delete-priority-scheme-modal';
import { Filter, type FilterProps } from './filter';
import messages from './messages';
import { AddOrEditPrioritySchemeModal } from './priority-scheme-modal';
import { PrioritySchemesHeader } from './priority-schemes-header';
import { PrioritySchemesTable } from './priority-schemes-table';

export type PrioritySchemePageProps = Omit<
	PrioritySchemesPageControllerReturn,
	'onDeleteSchemeSuccess'
> &
	DeletePrioritySchemeProps &
	FilterProps;

const PrioritySchemesTableContent = (props: PrioritySchemePageProps) => {
	const { formatMessage } = useIntl();

	const { locked, state } = props;

	switch (state) {
		case PrioritySchemesPageState.ERROR:
			return (
				<ErrorStateOriginal
					header={formatMessage(messages.errorHeader)}
					description={formatMessage(messages.errorDescription)}
				/>
			);
		case PrioritySchemesPageState.LOADING:
		case PrioritySchemesPageState.INITITAL:
			return <PrioritySchemesTable isLoading isLocked={false} {...props} />;
		case PrioritySchemesPageState.SUCCESS:
			return <PrioritySchemesTable isLoading={false} isLocked={locked} {...props} />;
		default:
			return null;
	}
};

export const PrioritySchemesPageView = (props: PrioritySchemePageProps) => {
	const { formatMessage } = useIntl();

	const {
		locked,
		prioritySchemeModalPartialProps,
		onComplete,
		onAddScheme,
		state,
		deleteModalProps,
		onSubmitDeleteScheme,
		onCompleteDeleteScheme,
		filters,
		page: { totalItems },
		setSelectedPriorityIds,
		setSchemeName,
	} = props;

	return (
		<Container>
			<ContextualAnalyticsData
				sourceName={PRIORITY_SCHEME_CONFIGURATION_SOURCE_NAME}
				sourceType={SCREEN}
			>
				<FireScreenAnalytics />
				<DocumentTitle title={formatMessage(messages.title)} />
				<ModalTransition>
					{prioritySchemeModalPartialProps.isOpen && (
						<AddOrEditPrioritySchemeModal
							{...prioritySchemeModalPartialProps}
							onComplete={onComplete}
						/>
					)}
				</ModalTransition>
				<ModalTransition>
					{deleteModalProps.isOpen && (
						<DeletePrioritySchemeModalWrapper
							associatedProjectCount={deleteModalProps.associatedProjectCount}
							isSubmitting={deleteModalProps.isSubmitting}
							schemeName={deleteModalProps.schemeName}
							onComplete={onCompleteDeleteScheme}
							onSubmit={onSubmitDeleteScheme}
						/>
					)}
				</ModalTransition>
				<PrioritySchemesHeader
					hasError={PrioritySchemesPageState.ERROR === state}
					isLoading={PrioritySchemesPageState.LOADING === state}
					isLocked={locked}
					limitSchemes={totalItems >= PRIORITY_SCHEMES_HARD_LIMIT}
					onAddScheme={onAddScheme}
				/>
				<Filter
					filters={filters}
					setSelectedPriorityIds={setSelectedPriorityIds}
					setSchemeName={setSchemeName}
				/>
				<PrioritySchemesTableContent {...props} />
			</ContextualAnalyticsData>
		</Container>
	);
};
